/** SUNTRONIX ONE — Administration → ONE CONNECT → Apple Business (P0). */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Apple, PlugZap, RefreshCw, ShieldCheck, Boxes, GitCompare, Sparkles } from "lucide-react";

const ST_TINT = {
  CONNECTED: "bg-emerald-100 text-emerald-800", SETUP_REQUIRED: "bg-amber-100 text-amber-800",
  AUTH_EXPIRED: "bg-rose-100 text-rose-800", DEGRADED: "bg-orange-100 text-orange-800",
  ERROR: "bg-rose-100 text-rose-800", DISABLED: "bg-slate-200 text-slate-500",
};
const TR = { PASS: "text-emerald-600", FAIL: "text-rose-600", NOT_ENABLED: "text-slate-400", "N/A": "text-slate-400" };
const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");

export default function AppleBusiness() {
  const [conns, setConns] = useState([]);
  const [health, setHealth] = useState([]);
  const [active, setActive] = useState(null);
  const [devices, setDevices] = useState([]);
  const [recon, setRecon] = useState(null);
  const [tab, setTab] = useState("devices");
  const [form, setForm] = useState({ tenant_id: "", organisation_name: "", sandbox: true, client_id: "", key_id: "", team_id: "", private_key_pem: "" });
  const [showConnect, setShowConnect] = useState(false);
  const [ask, setAsk] = useState({ q: "Which Apple devices are not linked to ONE", a: null });

  const load = useCallback(() => {
    api.get("/apple-business/connections").then(({ data }) => setConns(data.items || [])).catch(() => {});
    api.get("/apple-business/health").then(({ data }) => setHealth(data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openConn = async (c) => {
    setActive(c); setTab("devices");
    const { data } = await api.get(`/apple-business/devices?tenant_id=${c.tenant_id}`);
    setDevices(data.items || []);
    api.get(`/apple-business/connections/${c.id}/reconciliation`).then(({ data }) => setRecon(data)).catch(() => {});
  };

  const connect = async () => {
    if (!form.tenant_id) return toast.error("tenant_id (master_company_id) is required");
    try {
      const { data } = await api.post("/apple-business/connections", form);
      toast.success(`Connected (${data.mode}) — ${data.status}`); setShowConnect(false); load();
    } catch (e) { toast.error(JSON.stringify(e.response?.data?.detail) || "Failed"); }
  };
  const test = async (c) => {
    try { const { data } = await api.post(`/apple-business/connections/${c.id}/test`);
      const r = data.result;
      toast[data.status === "CONNECTED" ? "success" : "error"](`${data.status} — AUTH ${r.authentication} · ORG ${r.organisation_access} · DEV ${r.device_list} · AC ${r.applecare} · DMS ${r.device_management_services} · WRITE ${r.write_permissions}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const sync = async (c) => {
    try { toast.info("Sync started…");
      const { data } = await api.post(`/apple-business/connections/${c.id}/sync?full=true`);
      toast.success(`Sync ${data.status}: ${data.checked} checked · ${data.matched} matched · ${data.created} new · ${data.conflicts} conflicts`);
      load(); if (active?.id === c.id) openConn(c);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleWrites = async (c) => {
    try { const { data } = await api.post(`/apple-business/connections/${c.id}/writes`, { writes_enabled: !c.writes_enabled });
      toast.success(`Writes ${data.writes_enabled ? "ENABLED" : "disabled"}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Certify test first"); }
  };
  const runAsk = async () => {
    try { const { data } = await api.get(`/apple-business/ask?q=${encodeURIComponent(ask.q)}${active ? `&tenant_id=${active.tenant_id}` : ""}`);
      setAsk((s) => ({ ...s, a: data }));
    } catch (e) { toast.error("Ask failed"); }
  };

  return (
    <div className="mx-auto max-w-[1200px] space-y-4" data-testid="apple-business-page">
      <header className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-slate-900 text-white"><Apple className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="font-heading text-xl font-semibold">Apple Business</div>
          <div className="text-[11px] text-slate-500">Organisation devices → Golden Asset Record · tenant-isolated · external data is evidence, not truth</div>
        </div>
        <button onClick={() => setShowConnect((v) => !v)} data-testid="ab-connect-btn" className="rounded-md bg-slate-900 px-3 py-1.5 text-[11px] font-semibold text-white">Connect organisation</button>
      </header>

      {showConnect && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid="ab-connect-form">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px]">Tenant (master_company_id)<input data-testid="ab-tenant-input" className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-[12px]" value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })} /></label>
            <label className="text-[11px]">Organisation name<input className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-[12px]" value={form.organisation_name} onChange={(e) => setForm({ ...form, organisation_name: e.target.value })} /></label>
          </div>
          <label className="flex items-center gap-2 text-[11px] font-semibold"><input type="checkbox" data-testid="ab-sandbox-toggle" checked={form.sandbox} onChange={(e) => setForm({ ...form, sandbox: e.target.checked })} /> Sandbox (no credentials — safe pilot)</label>
          {!form.sandbox && (
            <div className="grid grid-cols-3 gap-2">
              <input placeholder="client_id" className="rounded border border-slate-200 px-2 py-1.5 text-[12px]" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} />
              <input placeholder="key_id" className="rounded border border-slate-200 px-2 py-1.5 text-[12px]" value={form.key_id} onChange={(e) => setForm({ ...form, key_id: e.target.value })} />
              <input placeholder="team_id" className="rounded border border-slate-200 px-2 py-1.5 text-[12px]" value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} />
              <textarea placeholder="Private key PEM (stored encrypted, server-side)" className="col-span-3 rounded border border-slate-200 px-2 py-1.5 font-mono text-[10px]" rows={3} value={form.private_key_pem} onChange={(e) => setForm({ ...form, private_key_pem: e.target.value })} />
            </div>
          )}
          <button onClick={connect} data-testid="ab-connect-save" className="rounded-md bg-emerald-700 px-3 py-1.5 text-[11px] font-semibold text-white">Save connection</button>
        </div>
      )}

      {/* Connections */}
      <div className="space-y-2" data-testid="ab-connections">
        {!conns.length && <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-slate-400 text-[12px]">No Apple Business connections yet. Connect a tenant organisation to begin.</div>}
        {conns.map((c) => {
          const h = health.find((x) => x.connection_id === c.id) || {};
          return (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`ab-conn-${c.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-slate-800">{c.organisation_name || c.tenant_id}</span>
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${ST_TINT[c.status] || "bg-slate-100"}`} data-testid={`ab-status-${c.id}`}>{c.status}</span>
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold text-slate-600">{c.mode}</span>
                {c.writes_enabled ? <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[9px] font-semibold text-indigo-700">WRITES ON</span> : <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] text-slate-400">read-only</span>}
                <span className="text-[9px] text-slate-400">last sync {fmt(h.last_successful_sync)} · next {fmt(h.next_sync)}</span>
                <div className="ml-auto flex gap-1.5">
                  <button onClick={() => test(c)} data-testid={`ab-test-${c.id}`} className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[10px] font-semibold text-slate-700"><PlugZap className="h-3 w-3" /> Test</button>
                  <button onClick={() => sync(c)} data-testid={`ab-sync-${c.id}`} className="inline-flex items-center gap-1 rounded border border-teal-700 px-2 py-1 text-[10px] font-semibold text-teal-800"><RefreshCw className="h-3 w-3" /> Sync</button>
                  <button onClick={() => toggleWrites(c)} data-testid={`ab-writes-${c.id}`} className="inline-flex items-center gap-1 rounded border border-indigo-300 px-2 py-1 text-[10px] font-semibold text-indigo-700"><ShieldCheck className="h-3 w-3" /> {c.writes_enabled ? "Disable writes" : "Enable writes"}</button>
                  <button onClick={() => openConn(c)} data-testid={`ab-open-${c.id}`} className="rounded bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white">Open</button>
                </div>
              </div>
              {h.last_run && <div className="mt-1 text-[9px] text-slate-400">Last run: {h.last_run.checked} checked · {h.last_run.created} new · {h.last_run.updated} updated · {h.last_run.conflicts} conflicts · {h.last_run.status}</div>}
            </div>
          );
        })}
      </div>

      {/* Detail: devices / reconciliation / ask */}
      {active && (
        <div className="rounded-xl border border-slate-200 bg-white" data-testid="ab-detail">
          <div className="flex gap-2 border-b border-slate-100 p-2">
            {[["devices", "Devices", Boxes], ["recon", "Reconciliation", GitCompare], ["ask", "Ask ONE", Sparkles]].map(([k, lbl, Icon]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`ab-tab-${k}`} className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-[11px] font-semibold ${tab === k ? "bg-slate-900 text-white" : "text-slate-600"}`}><Icon className="h-3.5 w-3.5" /> {lbl}</button>
            ))}
            <span className="ml-auto self-center text-[10px] text-slate-400">{active.organisation_name || active.tenant_id}</span>
          </div>

          {tab === "devices" && (
            <div className="overflow-x-auto"><table className="w-full text-[11px]">
              <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500"><tr>
                <th className="px-3 py-2">Serial</th><th className="px-3 py-2">Model</th><th className="px-3 py-2">Family</th><th className="px-3 py-2">Part #</th><th className="px-3 py-2">Colour</th><th className="px-3 py-2">Capacity</th><th className="px-3 py-2">Order date</th><th className="px-3 py-2">AppleCare</th><th className="px-3 py-2">Status</th>
              </tr></thead>
              <tbody>
                {devices.map((d) => { const a = d.attributes; return (
                  <tr key={d.apple_id} className="border-t border-slate-100" data-testid={`ab-device-${a.serialNumber}`}>
                    <td className="px-3 py-1.5 font-mono">{a.serialNumber}</td><td className="px-3 py-1.5">{a.deviceModel}</td>
                    <td className="px-3 py-1.5 text-slate-500">{a.productFamily}</td><td className="px-3 py-1.5 font-mono text-[10px]">{a.partNumber}</td>
                    <td className="px-3 py-1.5">{a.color}</td><td className="px-3 py-1.5">{a.deviceCapacity}</td>
                    <td className="px-3 py-1.5 text-[10px] text-slate-500">{a.orderDateTime ? new Date(a.orderDateTime).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-1.5">{a.appleCare?.status === "ACTIVE" ? <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">ACTIVE</span> : <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-1.5"><span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${a.releasedFromOrgDateTime ? "bg-slate-200 text-slate-500" : "bg-emerald-100 text-emerald-700"}`}>{a.releasedFromOrgDateTime ? "RELEASED" : "ACTIVE"}</span></td>
                  </tr>); })}
                {!devices.length && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">No devices synced yet. Run a sync.</td></tr>}
              </tbody>
            </table></div>
          )}

          {tab === "recon" && recon && (
            <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3" data-testid="ab-recon">
              {Object.entries(recon.counts).map(([cat, n]) => (
                <div key={cat} className={`rounded-lg border p-3 ${n && !["MATCHED"].includes(cat) ? "border-rose-200 bg-rose-50" : "border-slate-200"}`} data-testid={`ab-recon-${cat}`}>
                  <div className="text-xl font-heading font-semibold">{n}</div>
                  <div className="text-[9px] uppercase tracking-wide text-slate-500">{cat.replace(/_/g, " ")}</div>
                </div>
              ))}
            </div>
          )}

          {tab === "ask" && (
            <div className="space-y-2 p-3" data-testid="ab-ask">
              <div className="flex gap-2">
                <input className="flex-1 rounded border border-slate-200 px-2 py-1.5 text-[12px]" value={ask.q} onChange={(e) => setAsk({ ...ask, q: e.target.value })} data-testid="ab-ask-input" />
                <button onClick={runAsk} data-testid="ab-ask-run" className="rounded-md bg-slate-900 px-3 py-1.5 text-[11px] font-semibold text-white">Ask</button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {["Which Apple devices are not linked to ONE", "Which devices have active AppleCare", "Which devices have Apple data conflicts", "Show Apple devices by colour"].map((s) => (
                  <button key={s} onClick={() => setAsk({ q: s, a: null })} className="rounded-full border border-slate-200 px-2 py-0.5 text-[10px] text-slate-500">{s}</button>
                ))}
              </div>
              {ask.a && <pre className="rounded bg-slate-50 p-3 text-[11px]" data-testid="ab-ask-answer">{JSON.stringify(ask.a, null, 2)}</pre>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
